/**
 * Visual layout chooser.
 *
 * Deliberately not a `<select>` of strings like "1-3" / "2-1-1". Those labels
 * are meaningful to whoever wrote the schema and to nobody else; a volunteer
 * choosing a page layout is making a visual decision and needs to see the
 * shape. Each option renders a miniature built from the SAME proportions the
 * canvas grid uses (`layouts.ts`), so the picture cannot lie about the result.
 */
import React from 'react';

import { LAYOUTS } from '../layouts';
import type { ColumnLayout } from '../types';
import { cn } from '@/lib/utils';

interface LayoutChooserProps {
  value: ColumnLayout;
  onChange: (layout: ColumnLayout) => void;
  /**
   * How many elements switching to a given layout would relocate.
   *
   * A callback rather than a number, because the answer differs per option:
   * the useful moment to warn is while the user is looking at the narrower
   * layout, not after they have already applied it.
   */
  relocatingFor?: (layout: ColumnLayout) => number;
}

export function LayoutChooser({ value, onChange, relocatingFor }: LayoutChooserProps) {
  const [hovered, setHovered] = React.useState<ColumnLayout | null>(null);
  const previewed = hovered ?? value;
  const relocating = relocatingFor?.(previewed) ?? 0;
  return (
    <div>
      <span className="block text-sm font-semibold text-slate-800">Kolommen</span>
      <p className="mt-1 text-xs leading-relaxed text-slate-500">
        Op mobiel stapelen kolommen automatisch onder elkaar.
      </p>

      <div role="radiogroup" aria-label="Kolomindeling" className="mt-3 grid grid-cols-2 gap-2">
        {LAYOUTS.map((layout) => {
          const selected = layout.id === value;
          const loses = (relocatingFor?.(layout.id) ?? 0) > 0;
          return (
            <button
              key={layout.id}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={loses ? `${layout.label} (verplaatst inhoud)` : layout.label}
              title={layout.label}
              onClick={() => onChange(layout.id)}
              onMouseEnter={() => setHovered(layout.id)}
              onMouseLeave={() => setHovered((current) => (current === layout.id ? null : current))}
              onFocus={() => setHovered(layout.id)}
              onBlur={() => setHovered((current) => (current === layout.id ? null : current))}
              className={cn(
                'group rounded-xl border p-2 text-left transition',
                selected
                  ? 'border-teal-400 bg-teal-50/60 ring-1 ring-teal-300'
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50',
              )}
            >
              {/* The miniature is the label. The words underneath are a
                  fallback for screen readers and for anyone who wants the
                  proportion spelled out -- never the primary signal. */}
              <span
                aria-hidden="true"
                className="flex h-9 w-full gap-1 rounded-lg bg-white p-1 ring-1 ring-inset ring-slate-200"
              >
                {layout.parts.map((part, index) => (
                  <span
                    key={index}
                    style={{ flexGrow: part }}
                    className={cn(
                      'rounded-[3px] transition',
                      selected ? 'bg-teal-400/70' : 'bg-slate-200 group-hover:bg-slate-300',
                    )}
                  />
                ))}
              </span>
              <span className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-slate-600">
                <span className="truncate">{layout.label}</span>
                {loses ? <span aria-hidden="true" className="text-amber-500">•</span> : null}
              </span>
            </button>
          );
        })}
      </div>

      {relocating > 0 ? (
        // Said before the click, not after. Relocation is safe and undoable,
        // but a user who watches two columns of their work jump elsewhere
        // without warning has no way to know it was deliberate.
        <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">
          Bij een smallere indeling verplaatsen{' '}
          <strong>
            {relocating} {relocating === 1 ? 'onderdeel' : 'onderdelen'}
          </strong>{' '}
          naar de laatste kolom. Er gaat niets verloren.
        </p>
      ) : null}
    </div>
  );
}
